import React, { useState, useRef, useEffect } from "react"
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Stack,
    Typography,
    Paper,
    Divider,
    CircularProgress,
    Alert,
} from "@mui/material"
import DashboardCard from "../../components/Layouts/DashboardCard/DashboardCard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faArrowLeft,
    faArrowRightToBracket,
    faUserPlus,
} from "@fortawesome/free-solid-svg-icons"
import RangeDatePicker from "../../components/UI/AppDatePicker/AppDatePicker"
import Header from "../../components/Layouts/AppHeader/AppHeader"
import { FormDatasSubmission } from "../../api/FormData/FormDataSubmissionApi"
import VisitorTable from "../../components/Layouts/VisitorsTable/VisitorsFile"
import { useNavigate } from "react-router-dom"

interface VisitorFormData {
    id: string
    visitorName: string
    visitDate: string
    contactNumber: string
    govtIdNumber: string
    photoId: string
    entryTime: string
    exitTime: string
    address: string
    toMeet: string
    purpose: string
    status: string
}
const formatDate = (date: Date | null): string => {
    if (!date) return ""
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = String(date.getFullYear()).slice(-2)
    return `${day}-${month}-${year}`
}
const DashboardPage: React.FC = () => {
    const [open, setOpen] = useState(false)
    const getCurrentTime = () => {
        const now = new Date()
        const hours = String(now.getHours()).padStart(2, "0")
        const minutes = String(now.getMinutes()).padStart(2, "0")
        return `${hours}:${minutes}`
    }
  
    const [visitorData, setVisitorData] = useState<any[]>([])
    const [visitorStats, setVisitorStats] = useState({
        totalVisitors: 0,
        visitorsInside: 0,
        visitorsOutSide: 0,
    })
    const calculateRelativeDate = (days: number): Date => {
        const currentDate = new Date()
        currentDate.setDate(currentDate.getDate() - days)
        return currentDate
    }

    const [insideVisitors, setInsideVisitors] = useState<VisitorFormData[]>([])
    const [exitedVisitors, setExitedVisitors] = useState<VisitorFormData[]>([])
    const [showInside, setShowInside] = useState<boolean>(true)
    const [exitModalOpen, setExitModalOpen] = useState(false)
    const [selectedVisitor, setSelectedVisitor] =
        useState<VisitorFormData | null>(null)
    const [exitTime, setExitTime] = useState("")
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [uploading, setUploading] = useState<boolean>(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [startDate, setStartDate] = useState<Date | null>(
        calculateRelativeDate(9)
    )
    const [endDate, setEndDate] = useState<Date | null>(
        calculateRelativeDate(0)
    )
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const navigate = useNavigate()
    const redirectToOtp = () => navigate("/verify-mobile")

    const handleShowInside = () => {
        setShowInside(true)
    }

    const handleShowExited = () => {
        setShowInside(false)
    }

    const exportToCSV = () => {
        const headers = [
            "ID",
            "Name",
            "Visit Date",
            "Contact Number",
            "Government ID",
            "Entry Time",
            "Exit Time",
            "Address",
            "To Meet",
            "Purpose",
            "Status",
        ]

        const rows = visitorData.map((visitor) => [
            visitor.id,
            visitor.visitorName,
            visitor.visitDate,
            visitor.contactNumber,
            visitor.govtIdNumber,
            visitor.entryTime,
            visitor.exitTime,
            visitor.address,
            visitor.toMeet,
            visitor.purpose,
            visitor.status,
        ])

        const csvContent = [
            headers.join(","),
            ...rows.map((row) => row.map((value) => `"${value}"`).join(",")),
        ].join("\n")

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url

        const filename = showInside
            ? "Entered_Visitors.csv"
            : "Exited_Visitors.csv"
        link.setAttribute("download", filename)

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    useEffect(() => {
        const fetchVisitorStats = async () => {
            try {
                const stats = await FormDatasSubmission.api.getVisitorStats()
                setVisitorStats(stats.data)
                const response = await FormDatasSubmission.api.getVisitorsData(
                    showInside ? "ENTERED" : "EXITED",
                    startDate,
                    endDate
                )

                // Ensure response.data is an array
                if (Array.isArray(response.data)) {
                    setVisitorData(response.data as VisitorFormData[])
                } else {
                    console.error("Expected an array of visitor data")
                }
            } catch (error) {
                console.error("Failed to fetch visitor stats", error)
            }
        }

        fetchVisitorStats()
    }, [showInside, startDate, endDate])

    const handleOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const handleExitClick = (visitor: any) => {
        const visitorData: VisitorFormData = {
            id: visitor.id,
            visitorName: visitor.visitorName,
            visitDate: visitor.visitDate,
            contactNumber: visitor.contactNumber,
            govtIdNumber: visitor.govtIdNumber,
            photoId: visitor.photoId,
            entryTime: visitor.entryTime,
            exitTime: visitor.exitTime,
            address: visitor.address,
            toMeet: visitor.toMeet,
            purpose: visitor.purpose,
            status: visitor.status,
        }
        setSelectedVisitor(visitorData)
        setExitModalOpen(true)
    }

    const handleExitSubmit = async () => {
        if (selectedVisitor) {
            const updatedVisitor: VisitorFormData = {
                ...selectedVisitor,
                exitTime: getCurrentTime(),
                status: "EXITED",
            }
            try {
                await FormDatasSubmission.api.updateVisitorInfo(
                    updatedVisitor.id,
                    updatedVisitor.status,
                    updatedVisitor
                )
                setExitedVisitors((prev) => [...prev, updatedVisitor])
                setInsideVisitors((prev) =>
                    prev.filter((v) => v.id !== selectedVisitor.id)
                )
                setExitModalOpen(false)
                setSelectedVisitor(null)
                setExitTime("")
                window.location.reload()
            } catch (error) {
                console.error("Failed to update visitor info", error)
            }
        }
    }
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (exitModalOpen) {
            interval = setInterval(() => {
                setExitTime(getCurrentTime());
            }, 1000); 
        } else {
            setExitTime(""); 
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [exitModalOpen]);
    
    const handleDateChange = (dates: [Date | null, Date | null]) => {
        setStartDate(dates[0])
        setEndDate(dates[1])
    }

    return (
        <>
            <Header />
            <Box className="w-full px-4 sm:px-6 lg:px-8">
                {/* Dashboard Cards */}
                <Box className="mt-3 flex justify-between lg:m-5 items-center">
                    <div className="flex">
                        <p className="font-bold">Today Visitor Log</p>
                    </div>
                    <div className="flex">
                        <Button
                            variant="outlined"
                            onClick={redirectToOtp}
                            sx={{
                                backgroundColor: "#3ea787",
                                color: "#fff",
                                padding: "5px 10px",
                                fontSize: "14px",
                                "&:hover": {
                                    backgroundColor: "#17B34A",
                                },
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faUserPlus}
                                className="mr-2"
                            />
                            Add Visitor
                        </Button>
                    </div>
                </Box>
                <div className="m-5">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <DashboardCard
                                heading="Visitors Inside"
                                backgroundColor="#37A483"
                                data={visitorStats.visitorsInside?.toString() || "0"}
                                comparison="-5%"
                                imgSrc="/path/to/image2.png"
                                increment={false}
                                loading={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <DashboardCard
                                heading="Visitors Outside"
                                backgroundColor="#FF5246"
                                data={visitorStats.visitorsOutSide?.toString() || "0"}
                                comparison="+20%"
                                imgSrc="/path/to/image3.png"
                                increment={true}
                                loading={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <DashboardCard
                                heading="Total Visitors"
                                backgroundColor="#6068FF"
                                data={visitorStats.totalVisitors?.toString() || "0"}
                                comparison="+10%"
                                imgSrc="/path/to/image1.png"
                                increment={true}
                                loading={false}
                            />
                        </Grid>
                    </Grid>

                    {/* Add Visitor Button */}
                    <hr className="my-2" />
                    <Grid item xs={12}>
                        <div className="w-full flex flex-col sm:flex-row text-[#455A64] font-rubik items-center">
                            <div className="flex w-full items-center lg:justify-end mt-2 sm:mt-0">
                                <div className="flex items-center mr-3">
                                    <p className="mr-2 font-bold">
                                        <small className="md:text-base">
                                            {" "}
                                            Previous Visitor
                                        </small>
                                    </p>
                                    <RangeDatePicker
                                        onChange={handleDateChange}
                                        maxDaysAdd={0}
                                        minDaysSub={92}
                                        defaultStartDate={0}
                                        defaultEndDate={0}
                                    />
                                </div>

                                <Button
                                    variant="outlined"
                                    onClick={exportToCSV}
                                    sx={{
                                        backgroundColor: "#3ea787",
                                        color: "#fff",
                                        padding: "5px",
                                        fontSize: "14px",
                                        "&:hover": {
                                            backgroundColor: "#17B34A",
                                        },
                                    }}
                                >
                                    Export
                                </Button>
                            </div>
                        </div>
                        <hr className="my-2" />
                    </Grid>

                    
                    <div className="flex  mb-3">
                        <Button
                            variant="outlined"
                            onClick={handleShowInside}
                            sx={{
                                backgroundColor: "#3ea787",
                                color: "#fff",
                                padding: "5px 10px",
                                fontSize: "14px",
                                "&:hover": {
                                    backgroundColor: "#17B34A",
                                },
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faArrowRightToBracket}
                                className="mr-2"
                            />
                            Visitor Inside
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleShowExited}
                            sx={{
                                border: "1px solid red",
                                color: "red",
                                padding: "5px 10px",
                                fontSize: "14px",
                                marginLeft: "10px",
                            }}
                        >
                            <img
                                src="/assets/arrow-right-to-bracket-solid.svg"
                                width={15}
                            />
                            Visitor Exited
                        </Button>
                    </div>

                    <VisitorTable
                        status={showInside ? "ENTERED" : "EXITED"}
                        startDate={formatDate(startDate)}
                        endDate={formatDate(endDate)}
                        onExitClick={handleExitClick}
                        visitorData={visitorData}
                    />

                    <Dialog
                        open={exitModalOpen}
                        onClose={() => setExitModalOpen(false)}
                        fullWidth
                        maxWidth="sm"
                        sx={{
                            "& .MuiDialog-paper": {
                                borderRadius: "12px",
                                padding: "20px",
                                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                            },
                        }}
                    >
                        <DialogTitle
                            sx={{
                                textAlign: "center",
                                fontWeight: "bold",
                                fontSize: "1.5rem",
                                color: "#3ea787",
                            }}
                        >
                            Confirm Visitor Exit
                        </DialogTitle>
                        <DialogContent>
                            <Typography
                                sx={{
                                    textAlign: "center",
                                    fontSize: "1.1rem",
                                    marginBottom: "20px",
                                }}
                            >
                                Are you sure you want to mark this visitor as
                                exited?
                            </Typography>
                            <TextField
                                label="Exit Time"
                                name="exitTime"
                                type="time"
                                value={exitTime || getCurrentTime()}
                                onChange={(e) => setExitTime(e.target.value)}
                                fullWidth
                                sx={{
                                    "& .MuiInputBase-root": {
                                        borderRadius: "8px",
                                    },
                                }}
                                disabled
                            />
                        </DialogContent>
                        <DialogActions
                            sx={{
                                justifyContent: "center",
                                padding: "16px",
                            }}
                        >
                            <Button
                                onClick={() => setExitModalOpen(false)}
                                variant="outlined"
                                sx={{
                                    color: "#f44336",
                                    borderColor: "#f44336",
                                    "&:hover": {
                                        backgroundColor: "#f44336",
                                        color: "#fff",
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleExitSubmit}
                                variant="contained"
                                sx={{
                                    backgroundColor: "#1FCD5B",
                                    "&:hover": {
                                        backgroundColor: "#17B34A",
                                    },
                                    marginLeft: "10px",
                                }}
                            >
                                Submit Exit
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            </Box>
        </>
    )
}

export default DashboardPage
